import React, { useState, useMemo, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
    DialogTrigger,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { toast } from "sonner";
import { ClipLoader } from "react-spinners";
import { formatUtcToUserTimezone } from "../lib/timezone";
import {
    User,
    Mail,
    BookOpenIcon,
    Clock,
    ExternalLink,
    File,
    Github,
} from "lucide-react";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";

const feedbackTemplates = [
    "Excellent Work",
    "Good Effort",
    "Great UI/UX Design",
    "Needs Revision",
];

export default function UpdateReviewDialog({
    open,
    onOpenChange,
    submission,
    existingReview,
    gradedBy,
    onReviewUpdated,
    onSaved,
    courseName,
    userFirstName,
    userLastName,
    userEmail,
}) {
    const normalizeExistingReview = (value) => {
        if (!value || typeof value !== "object") return null;
        const nested = value.review && typeof value.review === "object" ? value.review : null;

        const scoreRaw = value.score ?? nested?.score;
        const scoreParsed = typeof scoreRaw === "string" ? Number(scoreRaw) : scoreRaw;
        const normalizedScore =
            typeof scoreParsed === "number" && Number.isFinite(scoreParsed)
                ? scoreParsed
                : scoreParsed ?? null;

        return {
            ...value,
            id: value.id ?? nested?.id ?? null,
            reviewed_at: value.reviewed_at ?? nested?.reviewed_at ?? null,
            score: normalizedScore,
            feedback: value.feedback ?? nested?.feedback ?? "",
            is_passed: value.is_passed ?? nested?.is_passed,
            reviewer: value.reviewer ?? nested?.reviewer,
            review: nested ?? value.review,
        };
    };

    const normalizedExistingReview = useMemo(
        () => normalizeExistingReview(existingReview),
        [existingReview]
    );

    const submissionData = useMemo(() => {
        if (!submission) {
            return {
                id: null,
                submissionId: null,
                studentId: null,
                studentName: "Not available",
                studentEmail: "Not available",
                submissionDate: "Not available",
                course: courseName ?? "Not available",
                projectId: null,
                projectTitle: "Not available",
                projectDescription: "Not available",
                projectType: "Not available",
                passmark: 0,
                maxPoints: 100,
                score: 0,
                feedback: "",
                githubUrl: null,
                liveUrl: null,
                file_url: null,
            };
        }

        const reviewScore =
            normalizedExistingReview?.score ??
            submission.best_score ??
            submission.score ??
            0;
        const reviewFeedback =
            normalizedExistingReview?.feedback ??
            submission.feedback ??
            "";

        return {
            id: null,
            submissionId: null,
            studentId:
                submission.user_id ??
                submission.student_id ??
                submission.studentId ??
                null,
            studentName:
                submission.student_name ??
                submission.studentName ??
                (userFirstName && userLastName
                    ? `${userFirstName} ${userLastName}`
                    : "Not available"),
            studentEmail:
                submission.student_email ??
                submission.studentEmail ??
                userEmail ??
                "Not available",
            submissionDate: submission.submitted_on
                ? formatUtcToUserTimezone(submission.submitted_on)
                : "Not available",
            course:
                submission.project?.course ??
                submission.project?.topic_name ??
                courseName ??
                "Not available",
            projectId: submission.project_id ?? submission.project?.id ?? null,
            projectTitle: submission.project?.title ?? "Not available",
            projectDescription: submission.project?.description ?? "Not available",
            projectType: submission.project?.type ?? "standard",
            passmark: submission.project?.passmark ?? 50,
            maxPoints: submission.project?.score ?? 100,
            score: reviewScore,
            feedback: reviewFeedback,
            githubUrl:
                submission.submission_url ??
                submission.githubUrl ??
                submission.github_repo ??
                null,
            liveUrl: submission.liveUrl ?? submission.live_url ?? null,
            file_url: submission.file_url ?? submission.file ?? null,
        };
    }, [submission, normalizedExistingReview, courseName, userFirstName, userLastName, userEmail]);

    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(submissionData.feedback);
    const [loading, setLoading] = useState(false);
    const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(feedbackTemplates[0]);
    const [passmark, setPassmark] = useState(submissionData.passmark ?? 0);
    const [description, setDescription] = useState(submissionData.projectDescription ?? "");
    const { getToken, userId } = useAuth();

    const actualPassmark = useMemo(() => {
        return Math.round(((passmark ?? 50) / 100) * (submissionData.maxPoints ?? 100));
    }, [passmark, submissionData.maxPoints]);

    useEffect(() => {
        if (open) {
            setScore(submissionData.score ?? 0);
        }
        // setScore(submissionData.score ?? 0);
        setFeedback(submissionData.feedback);
        setSelectedTemplate(feedbackTemplates[0]);
        setPassmark(submissionData.passmark ?? 0);
        setDescription(submissionData.projectDescription ?? "");
    }, [submissionData, open]);


    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setFeedback(template);
    };

    const handleOpenMarkdownEditor = (e) => {
        e?.preventDefault?.();
        setMarkdownEditorOpen(true);
    };

    const handleMarkdownSave = (newContent) => {
        setFeedback(newContent);
        setMarkdownEditorOpen(false);
    };

    const handleSubmit = async () => {
        if (!feedback.trim()) {
            toast.error("Feedback is required");
            return;
        }
        const grader = userId;
        if (!grader) {
            toast.error("Graded by user ID is required");
            return;
        }
        if (!submissionData.studentId || !submissionData.projectId) {
            toast.error("Student ID and Project ID are required");
            return;
        }
        setLoading(true);
        const reviewData = {
            student_id: String(submissionData.studentId),
            project_id: String(submissionData.projectId),
            reviewed_by: String(grader),
            graded_by: String(grader),
            feedback: feedback,
            score: parseFloat(score),
            is_passed: score >= (actualPassmark || 50),
        };

        if (existingReview?.id) {
            reviewData.review_id = existingReview.id;
        }

        console.log("Update review payload", reviewData)
        try {
            const token = await getToken();
            if (!token) return;
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            };
            const reviewResponse = await axios.post(
                api_endpoint + `/project/review`,
                reviewData,
                reqConfig
            );
            console.log("Review response:", reviewResponse);
            if (reviewResponse.status >= 200 && reviewResponse.status < 300) {
                toast.success("Review updated successfully!");
                onOpenChange(false);
                if (onSaved) onSaved(reviewResponse.data);
                if (onReviewUpdated) onReviewUpdated();
            } else {
                toast.error("Failed to update review");
            }
        } catch (error) {
            toast.error("Failed to update review");
            console.error("Error updating review:", error);
        } finally {
            setLoading(false);
        }
    };
    const denom = submissionData.maxPoints > 0 ? submissionData.maxPoints : 100;
    const fillPercent = Math.min(100, Math.max(0, (score / denom) * 100));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="text-(--neutral-900)">
                <DialogHeader className="">
                    <DialogTitle className="font-heading text-xl text-(--neutral-900)">
                        Update Review
                    </DialogTitle>
                    <div className="text-sm text-(--neutral-600)">
                        {submissionData.projectTitle !== "Not available" && (
                            <div className="mb-1 font-body font-medium text-(--neutral-800)">
                                {submissionData.projectTitle}
                            </div>
                        )}
                        <div className="font-body">
                            Update your feedback and score for this project
                        </div>
                    </div>
                </DialogHeader>
                <div className="">
                    {/* Submission Details */}
                    <div className="mb-8">
                        <h3 className="mb-4 text-xl font-bold font-heading text-(--neutral-900)">
                            Submission Details
                        </h3>
                        <div className="">
                            <div className="flex items-start gap-3 px-4 py-3">
                                <Clock className="w-5 h-5 text-(--neutral-500) mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <span className="font-medium text-(--neutral-700) font-heading">
                                        Submitted On:
                                    </span>
                                    <span className="ml-1 text-(--neutral-900) font-body">
                                        {submissionData.submissionDate || "Not available"}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 px-4 py-3">
                                <User className="w-5 h-5 text-(--neutral-500) mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <span className="font-medium text-(--neutral-700) font-heading">
                                        Submitted By:
                                    </span>
                                    <span className="ml-1 text-(--neutral-900) font-body">
                                        {submissionData.studentName || "Not available"}
                                        {submissionData.studentEmail && submissionData.studentEmail !== "Not available"
                                            ? ` · ${submissionData.studentEmail}`
                                            : ""}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 px-4 py-3">
                                <BookOpenIcon className="w-5 h-5 text-(--neutral-500) mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <span className="font-medium text-(--neutral-700) font-heading">
                                        Associated Course:
                                    </span>
                                    <span className="ml-1 text-(--neutral-900) font-body">
                                        {submissionData.course || "Not available"}
                                    </span>
                                </div>
                            </div>

                            <div className="border-b border-(--neutral-300)" />

                            <div className="flex items-start gap-3 px-4 py-3">
                                <Github className="w-5 h-5 text-(--neutral-500) mt-0.5 shrink-0" />
                                <div className="text-sm min-w-0">
                                    <span className="font-medium text-(--neutral-700) font-heading">
                                        Github Repository:
                                    </span>
                                    <span className="ml-1">
                                        {submissionData.githubUrl ? (
                                            <a
                                                href={submissionData.githubUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 break-all font-body text-(--neutral-700) hover:text-(--color-accent)"
                                            >
                                                {submissionData.githubUrl}
                                                <ExternalLink className="w-4 h-4 shrink-0" />
                                            </a>
                                        ) : (
                                            <span className="font-body text-(--neutral-400)">Not available</span>
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 px-4 py-3">
                                <ExternalLink className="w-5 h-5 text-(--neutral-500) mt-0.5 shrink-0" />
                                <div className="text-sm min-w-0">
                                    <span className="font-medium text-(--neutral-700) font-heading">
                                        Live URL:
                                    </span>
                                    <span className="ml-1">
                                        {submissionData.liveUrl ? (
                                            <a
                                                href={submissionData.liveUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 break-all font-body text-(--neutral-700) hover:text-(--color-accent)"
                                            >
                                                {submissionData.liveUrl}
                                                <ExternalLink className="w-4 h-4 shrink-0" />
                                            </a>
                                        ) : (
                                            <span className="font-body text-(--neutral-400)">Not available</span>
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 px-4 py-3">
                                <File className="w-5 h-5 text-(--neutral-500) mt-0.5 shrink-0" />
                                <div className="text-sm min-w-0">
                                    <span className="font-medium text-(--neutral-700) font-heading">
                                        Submitted File/Folder:
                                    </span>
                                    <span className="ml-1">
                                        {submissionData.file_url ? (
                                            <a
                                                href={submissionData.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 break-all font-body text-(--neutral-700) hover:text-(--color-accent)"
                                            >
                                                {submissionData.file_url}
                                                <ExternalLink className="w-4 h-4 shrink-0" />
                                            </a>
                                        ) : (
                                            <span className="font-body text-(--neutral-400)">No file submitted</span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Instructor Feedback */}
                    <div>
                        <h3 className="mb-4 text-xl font-bold font-heading text-(--neutral-900)">
                            Instructor Feedback
                        </h3>
                        <div className="mb-6">
                            <label className="mb-2 block font-heading font-medium text-(--neutral-700)">
                                Score* (Passmark: {actualPassmark})
                            </label>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="number"
                                    min={0}
                                    max={submissionData.maxPoints}
                                    value={score}
                                    onChange={(e) => {
                                        let val = Number(e.target.value);
                                        if (isNaN(val)) val = 0;
                                        if (val < 0) val = 0;
                                        if (val > submissionData.maxPoints) val = submissionData.maxPoints;
                                        setScore(val);
                                    }}
                                    className="h-12 w-24 border-(--neutral-300) text-center text-lg font-medium font-body"
                                />
                                <div className="flex-1">
                                    <input
                                        type="range"
                                        min={0}
                                        max={submissionData.maxPoints}
                                        value={score}
                                        onChange={(e) => setScore(Number(e.target.value))}
                                        className="w-full h-2 rounded-lg bg-(--neutral-200) appearance-none cursor-pointer"
                                        style={{
                                            background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${fillPercent}%, var(--neutral-200) ${fillPercent}%, var(--neutral-200) 100%)`,
                                        }}
                                    />
                                </div>
                                <span className="min-w-15 text-lg font-medium text-(--neutral-900)">
                                    {score}/{submissionData.maxPoints}
                                </span>
                            </div>
                            <div className="mt-1 text-xs font-body text-(--neutral-500)">
                                Pass: {score >= actualPassmark ? "✅" : "❌"} (Need {actualPassmark} + to pass)
                            </div>
                        </div>
                        <div className="mb-6">
                            <div className="mb-2 flex items-center justify-between">
                                <label className="mb-2 block font-heading font-medium text-(--neutral-700)">
                                    Detailed Feedback *
                                </label>
                                {/* <Button
                                    onClick={handleOpenMarkdownEditor}
                                    size="sm"
                                    variant="outline"
                                    className="btn-outline-sm hover:bg-blue hover:text-white hover:border-transparent rounded-[4px] font-heading"
                                >
                                    Edit in Fullscreen
                                </Button> */}
                                {/* Markdown Editor Modal */}
                                {/* <MarkdownEditorDialog
                                    open={markdownEditorOpen}
                                    initialValue={feedback}
                                    onClose={() => setMarkdownEditorOpen(false)}
                                    onSave={handleMarkdownSave}
                                /> */}
                            </div>
                            <Textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                rows={8}
                                placeholder="Update your detailed feedback here"
                                className="border-(--neutral-300) resize-none font-body"
                            />
                        </div>
                        {/* Quick Feedback Templates */}
                        <div className="mb-6">
                            <label className="mb-3 block font-heading font-medium text-(--neutral-700)">
                                Quick Feedback Templates
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                {feedbackTemplates.map((template) => (
                                    <Button
                                        variant="outline"
                                        key={template}
                                        className={`flex items-center justify-start gap-3 border px-4 py-3 text-left font-body transition-all ${selectedTemplate === template
                                            ? "border-(--color-primary)"
                                            : "border-(--neutral-300) hover:border-(--color-accent)"
                                            }`}
                                        onClick={() => handleTemplateSelect(template)}
                                    >
                                        <div
                                            className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${selectedTemplate === template
                                                ? "border-(--color-primary)"
                                                : "border-(--neutral-300) hover:border-(--color-accent)"
                                                }`}
                                        >
                                            {selectedTemplate === template && (
                                                <div className="h-2.5 w-2.5 rounded-full bg-(--color-primary)" />
                                            )}
                                        </div>
                                        <span className="text-(--neutral-700)">{template}</span>
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <DialogFooter className="">
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            className=""
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSubmit}
                        variant=""
                        className=""
                        disabled={loading || !feedback.trim()}
                    >
                        {loading ? <ClipLoader size={24} color="#fff" /> : "Update Review"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}